var domFunctions = require('../utils/_domFunctions');
var urlUtils = require('../utils/_urlUtils');
import debounce from '../utils/_debounce';

const MIN_LETTERS = 3;
const TIMEOUT = 400;

class mAddressSuggestions extends HTMLInputElement {
    constructor() {
        super();
        this.autocompleteEnabled = ('gmapEnabled' in this.dataset);
        this.addressSuggestionsUrl = this.dataset.addressSuggestionsUrl;
        this.countrySelector = this.form.querySelector('[data-component-country]');
        this.addressType = this.dataset.addressType;
        this.suggestions = this.form.querySelector('[data-component-suggestions]');
        this.placeIdField = this.form.querySelector('[data-component-place-id]');
        this.autocompleteFields = this.form.querySelectorAll('[data-autocomplete]');
        this.actualSuggestions = [];
        this.removeActiveInputClass = this.removeActiveInputClass.bind(this);
        this.timeout = null;
        this.processing = false;
        this.fetchSuggestions = this.fetchSuggestions.bind(this);
    }

    connectedCallback() {
        this.onKeyUp();
        this.closest('.input-wrapper').addEventListener('focusout', (e) => {
            if (!e.relatedTarget || !e.relatedTarget.classList.contains('single-suggestion')) {
                this.suggestions.innerHTML = '';
                this.classList.remove('displaying-suggestions');
            }
        });
        this.addEventListener('focusout', (e) => {
            this.removeActiveInputClass(e);
        });

        this.addEventListener('autocomplete:toggle', (e) => {
            if (e && e.detail && e.detail.autocompleteEnabled) {
                this.autocompleteEnabled = true;
            } else {
                this.autocompleteEnabled = false;
                this.setFieldLoading(false);
                this.classList.remove('displaying-suggestions');
            }
        });
    }

    onKeyUp() {
        this.addEventListener('keyup', this.abortFetch); // non debounced
        this.addEventListener('keyup', debounce(this.fetchSuggestions, TIMEOUT)); // debounced
    }

    abortFetch() {
        if (!this.autocompleteEnabled) {
            return;
        }
        if (this.controller) {
            this.controller.abort();
        }

        if (this.value.length > MIN_LETTERS) {
            this.classList.remove('displaying-suggestions');
            if (this.suggestions) this.suggestions.innerHTML = '';
            this.setFieldLoading(true);
        } else {
            this.setFieldLoading(false);
        }
    }

    setFieldLoading(boolean) {
        if (boolean) {
            this.parentElement.classList.add('loading');
            this.parentElement.previousElementSibling.classList.add('loading');
        } else {
            this.parentElement.classList.remove('loading');
            this.parentElement.previousElementSibling.classList.remove('loading');
        }
    }

    fetchSuggestions() {
        if (!this.autocompleteEnabled || this.value.length <= MIN_LETTERS) {
            return;
        }

        this.processing = true;
        var urlParams = {
            streetName: this.value,
            countryIso: this.countrySelector.value,
            requestType: 'ajaxload',
            addressType: this.addressType
        };
        var url = urlUtils.appendToUrl(this.addressSuggestionsUrl, urlParams);

        // controller to abort the fetch request see https://davidwalsh.name/cancel-fetch
        this.controller = new AbortController(); // eslint-disable-line no-undef
        const { signal } = this.controller;

        fetch(url, {
            method: 'GET',
            signal
        }).then(response => {
            return response.json();
        }).then(response => {
            this.suggestions.innerHTML = '';
            this.setFieldLoading(false);

            if (response.success) {
                domFunctions.replaceHtml(this.suggestions, response.renderedSuggestions);
                this.actualSuggestions = response.addressSuggestions;
                this.addEventSuggestions();
                this.classList.add('displaying-suggestions');
            }
            this.processing = false;
        }).catch(() => {}); // do nothing (this just prevents console error on aborted fetch)
    }

    removeActiveInputClass(e) {
        if (!document.hasFocus()) {
            this.classList.remove('displaying-suggestions');
        } else if (e.type === 'focusout' && e.relatedTarget && !e.relatedTarget.classList.contains('single-suggestion')) {
            this.classList.remove('displaying-suggestions');
        }
    }

    addEventSuggestions() {
        var suggestions = this.suggestions.querySelectorAll('.single-suggestion');
        suggestions.forEach((suggestion) => {
            suggestion.addEventListener('click', (e) => {
                this.onSuggestionSelect(e, suggestion);
            });
            suggestion.addEventListener('keydown', (e) => {
                if (e.code === 'Enter') {
                    this.onSuggestionSelect(e, suggestion);
                }
            });
        });
    }

    onSuggestionSelect(e, suggestion) {
        var index = suggestion.dataset.componentIndex;
        this.fillFormFields(this.actualSuggestions[index]);
        this.suggestions.innerHTML = '';
        this.removeActiveInputClass(e);
    }

    fillAddressFields(field, value) {
        // This regex allow us to not cut word if there is more characters than the max length
        var regex = new RegExp('(\\S.{0,' + (this.maxLength - 1) + '})(\\s+.*)?$');
        var splitValue = regex.exec(value);
        var address1Value = splitValue[1] && splitValue[1].trim();
        this.setFormField(field, address1Value || '');

        var address2 = this.form.querySelector('[name$="address2"]');
        if (address2) {
            var address2Value = splitValue[2] && splitValue[2].trim();
            this.setFormField(address2, address2Value || '');
        }
    }

    fillFormFields(suggestion) {
        for (var idx = 0; idx < this.autocompleteFields.length; idx++) {
            var formField = this.autocompleteFields[idx];
            var fieldName = formField.dataset.autocomplete;

            if (suggestion[fieldName]) {
                if (fieldName === 'address1') {
                    this.fillAddressFields(formField, suggestion[fieldName]);
                } else {
                    this.setFormField(formField, suggestion[fieldName]);
                }
            } else {
                this.setFormField(formField, '');
            }
        }
    }

    setFormField(formField, value) {
        formField.value = value;
        formField.dispatchEvent(new Event('change')); // trigger field validation
        formField.dispatchEvent(new Event('input')); // trigger floating label
    }
}

customElements.define('m-address-suggestions', mAddressSuggestions, {
    extends: 'input'
});
